import { useEffect, useMemo, useRef, useState } from 'react'
import { Spinner } from '@/components/Spinner'
import { downloadCsv } from '@/lib/csv'
import {
  fetchAddressHistory,
  probeAddressSituation,
  type HourlySlot,
} from '@/lib/energyHistory'

type Resolution = 'hourly' | 'daily'
type Status = 'idle' | 'loading' | 'done' | 'error'

const MAX_RANGE_DAYS = 366

/** Date → "YYYY-MM-DD" for <input type="date">. */
function toDateInput(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** "YYYY-MM-DD" → local midnight Date. */
function fromDateInput(s: string): Date {
  const [y, m, d] = s.split('-').map(Number)
  return new Date(y, m - 1, d)
}

interface ExportRow {
  time: string
  dateLabel: string
  timeLabel: string | null
  deliveredKwh: number
  returnedKwh: number
  producedKwh: number
}

export function EnergyExport({
  addressUuid,
  addressLabel,
}: {
  addressUuid: string
  addressLabel: string
}) {
  const today = useMemo(() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d
  }, [])
  const defaultFrom = useMemo(() => {
    const d = new Date(today)
    d.setDate(d.getDate() - 30)
    return d
  }, [today])

  const [fromInput, setFromInput] = useState(toDateInput(defaultFrom))
  const [toInput, setToInput] = useState(toDateInput(today))
  const [resolution, setResolution] = useState<Resolution>('daily')
  const datesTouchedRef = useRef(false)

  // Prefill the pickers with everything the address has, capped at one year.
  useEffect(() => {
    const controller = new AbortController()
    probeAddressSituation(addressUuid, controller.signal)
      .then((s) => {
        if (datesTouchedRef.current || !s.earliest) return
        const yearAgo = new Date(today)
        yearAgo.setFullYear(yearAgo.getFullYear() - 1)
        yearAgo.setDate(yearAgo.getDate() + 1)
        setFromInput(toDateInput(s.earliest > yearAgo ? s.earliest : yearAgo))
      })
      .catch(() => {})
    return () => controller.abort()
  }, [addressUuid, today])
  const [status, setStatus] = useState<Status>('idle')
  const [errorMessage, setErrorMessage] = useState('')
  const [slots, setSlots] = useState<HourlySlot[]>([])
  // The rows describe the fetched data, not the current form state.
  const [fetched, setFetched] = useState<{ from: Date; to: Date; resolution: Resolution } | null>(
    null
  )
  const [generatingPdf, setGeneratingPdf] = useState(false)
  const controllerRef = useRef<AbortController | null>(null)

  const validationError = useMemo(() => {
    const from = fromDateInput(fromInput)
    const to = fromDateInput(toInput)
    if (!fromInput || !toInput) return 'Kies een begin- en einddatum.'
    if (from > to) return 'De begindatum moet vóór de einddatum liggen.'
    const days = (to.getTime() - from.getTime()) / 86400000 + 1
    if (days > MAX_RANGE_DAYS) return 'Kies een periode van maximaal één jaar.'
    return null
  }, [fromInput, toInput])

  async function run() {
    if (validationError) return
    controllerRef.current?.abort()
    const controller = new AbortController()
    controllerRef.current = controller
    setStatus('loading')
    try {
      const from = fromDateInput(fromInput)
      // The picker's end date is inclusive; the API's toDate is exclusive.
      const toExclusive = fromDateInput(toInput)
      toExclusive.setDate(toExclusive.getDate() + 1)
      const result = await fetchAddressHistory(addressUuid, from, toExclusive, resolution, {
        signal: controller.signal,
      })
      setSlots(result)
      setFetched({ from, to: fromDateInput(toInput), resolution })
      setStatus('done')
    } catch (e) {
      if (controller.signal.aborted) return
      setErrorMessage(
        e instanceof Error && e.message
          ? e.message
          : 'We konden je energiedata niet ophalen. Probeer het later opnieuw.'
      )
      setStatus('error')
    }
  }

  const rows = useMemo<ExportRow[]>(() => {
    if (!fetched) return []
    const hourly = fetched.resolution === 'hourly'
    return slots.map((s) => {
      const d = new Date(s.time)
      return {
        time: s.time,
        dateLabel: d.toLocaleDateString('nl-NL'),
        timeLabel: hourly
          ? d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })
          : null,
        deliveredKwh: s.delivered / 1000,
        returnedKwh: s.returned / 1000,
        producedKwh: s.produced / 1000,
      }
    })
  }, [slots, fetched])

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => ({
          deliveredKwh: acc.deliveredKwh + r.deliveredKwh,
          returnedKwh: acc.returnedKwh + r.returnedKwh,
          producedKwh: acc.producedKwh + r.producedKwh,
        }),
        { deliveredKwh: 0, returnedKwh: 0, producedKwh: 0 }
      ),
    [rows]
  )

  function exportCsv() {
    if (!fetched) return
    const hourly = fetched.resolution === 'hourly'
    downloadCsv(
      `easyenergy-energiedata-${fromInput}-${toInput}.csv`,
      rows.map((r) => ({
        datum: r.dateLabel,
        ...(hourly ? { tijd: r.timeLabel } : {}),
        'afname (kWh)': r.deliveredKwh.toFixed(3),
        'teruglevering (kWh)': r.returnedKwh.toFixed(3),
        'opwek (kWh)': r.producedKwh.toFixed(3),
      }))
    )
  }

  async function exportPdf() {
    if (!fetched || generatingPdf) return
    setGeneratingPdf(true)
    try {
      // jsPDF is heavy; load it only when an export is actually requested.
      const { buildEnergyExportPdf } = await import('@/lib/energyExportPdf')
      const doc = await buildEnergyExportPdf({
        addressLabel,
        fromLabel: fetched.from.toLocaleDateString('nl-NL'),
        toLabel: fetched.to.toLocaleDateString('nl-NL'),
        resolution: fetched.resolution,
        generatedAt: new Date().toISOString(),
        totals,
        rows: rows.map((r) => ({
          label: r.timeLabel ? `${r.dateLabel} ${r.timeLabel}` : r.dateLabel,
          deliveredKwh: r.deliveredKwh,
          returnedKwh: r.returnedKwh,
          producedKwh: r.producedKwh,
        })),
      })
      doc.save(`easyenergy-energiedata-${fromInput}-${toInput}.pdf`)
    } finally {
      setGeneratingPdf(false)
    }
  }

  const inputCls = 'input w-auto'
  const loading = status === 'loading'

  return (
    <div className="space-y-5">
      {/* Period + resolution controls */}
      <div className="card p-5 md:p-[30px]">
        <div className="flex flex-wrap items-end gap-4">
          <label className="flex flex-col gap-1">
            <span className="field-label">Van</span>
            <input
              type="date"
              className={inputCls}
              value={fromInput}
              max={toDateInput(today)}
              onChange={(e) => {
                datesTouchedRef.current = true
                setFromInput(e.target.value)
              }}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="field-label">Tot en met</span>
            <input
              type="date"
              className={inputCls}
              value={toInput}
              max={toDateInput(today)}
              onChange={(e) => {
                datesTouchedRef.current = true
                setToInput(e.target.value)
              }}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="field-label">Resolutie</span>
            <select
              className={`${inputCls} pr-8`}
              value={resolution}
              onChange={(e) => setResolution(e.target.value as Resolution)}
            >
              <option value="daily">Per dag</option>
              <option value="hourly">Per uur</option>
            </select>
          </label>
          <button className="btn-primary" onClick={run} disabled={loading || !!validationError}>
            {loading ? (
              <>
                <Spinner className="size-4" /> Data ophalen…
              </>
            ) : status === 'done' ? (
              'Opnieuw ophalen'
            ) : (
              'Haal mijn data op'
            )}
          </button>
        </div>
        {validationError ? (
          <p className="mt-3 text-base font-semibold text-red">{validationError}</p>
        ) : (
          <p className="mt-3 max-w-3xl text-base leading-normal text-dark-blue/60">
            We halen de afname, teruglevering en opwek van je adres op over de gekozen periode
            (maximaal één jaar). Daarna download je de data als CSV of PDF.
          </p>
        )}
      </div>

      {status === 'error' && (
        <div className="card p-5 text-base font-semibold text-red">{errorMessage}</div>
      )}

      {status === 'done' && rows.length === 0 && (
        <div className="card-beige p-5 text-base leading-normal text-dark-blue/60 md:p-[30px]">
          We vonden geen meetdata voor deze periode. Kies een andere periode of controleer of je
          slimme meter is gekoppeld in de easyEnergy-app.
        </div>
      )}

      {status === 'done' && rows.length > 0 && (
        <>
          {/* Totals */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="card-beige p-5">
              <p className="field-label">Afname van het net</p>
              <p className="mt-1 text-28 font-semibold leading-tight text-dark-blue">
                {totals.deliveredKwh.toFixed(1)} kWh
              </p>
            </div>
            <div className="card-beige p-5">
              <p className="field-label">Teruglevering aan het net</p>
              <p className="mt-1 text-28 font-semibold leading-tight text-green">
                {totals.returnedKwh.toFixed(1)} kWh
              </p>
            </div>
            <div className="card-beige p-5">
              <p className="field-label">Eigen opwek</p>
              <p className="mt-1 text-28 font-semibold leading-tight text-dark-purple">
                {totals.producedKwh.toFixed(1)} kWh
              </p>
            </div>
          </div>

          {/* Downloads */}
          <div className="flex flex-col justify-between gap-4 rounded-3xl bg-dark-blue p-6 sm:flex-row sm:items-center md:p-[30px]">
            <div>
              <h3 className="text-xl font-semibold text-beige">Download je energiedata</h3>
              <p className="mt-1 max-w-xl text-base leading-normal text-beige/80">
                {rows.length.toLocaleString('nl-NL')} rijen over de periode{' '}
                {fetched?.from.toLocaleDateString('nl-NL')} t/m{' '}
                {fetched?.to.toLocaleDateString('nl-NL')}. CSV opent in Excel of Google Sheets;
                de PDF is een overzichtelijk rapport.
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <button className="btn-purple" onClick={exportCsv}>
                Download CSV
              </button>
              <button className="btn-purple" onClick={exportPdf} disabled={generatingPdf}>
                {generatingPdf ? (
                  <>
                    <Spinner className="size-4" /> PDF maken…
                  </>
                ) : (
                  'Download PDF'
                )}
              </button>
            </div>
          </div>

        </>
      )}
    </div>
  )
}
